import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Search, Download, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, Archive, RotateCcw, Users } from "lucide-react";
import ClientImport from "@/components/ClientImport";
import ProposalImport from "@/components/ProposalImport";
import { Checkbox } from "@/components/ui/checkbox";

export default function Clients() {
  const { user } = useAuth();
  const canWrite = user?.role !== "read_only";
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ legal_name: "", trading_name: "", client_type: "prospect", entity_type: "limited_company" });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [chConfigured, setChConfigured] = useState(false);
  const [chNum, setChNum] = useState("");
  const [chName, setChName] = useState("");
  const [chResults, setChResults] = useState([]);
  const [chBusy, setChBusy] = useState(false);
  const [chPending, setChPending] = useState({ count: 0, clients: [] });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [chExpanded, setChExpanded] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  const loadPending = () => api.get("/companies-house/pending").then((r) => {
    setChPending(r.data);
    if (searchParams.get("ch") === "1" && r.data.count > 0) {
      setChExpanded(true);
      setTimeout(() => document.getElementById("ch-pending-banner")?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    }
  }).catch(() => {});
  useEffect(() => { api.get("/companies-house/status").then((r) => { setChConfigured(r.data.configured); if (r.data.configured) loadPending(); }).catch(() => {}); }, []);

  const bulkSync = async (onlyPending = false) => {
    const label = onlyPending ? "clients with pending Companies House updates" : "all clients with a company number";
    if (!window.confirm(`Update ${label} from Companies House now? This refreshes company details and adds any new directors as contacts.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/companies-house/bulk-sync", { import_officers: true, directors_only: true, only_pending: onlyPending });
      const bits = [`Updated ${data.updated}/${data.total}`];
      if (data.date_changes) bits.push(`${data.date_changes} date change(s)`);
      if (data.new_contacts) bits.push(`${data.new_contacts} new contact(s)`);
      if (data.errors?.length) bits.push(`${data.errors.length} error(s)`);
      toast.success(bits.join(" · "));
      load(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk update failed"); }
    finally { setBulkBusy(false); }
  };

  const applyOne = async (cid) => {
    setApplyingId(cid);
    try {
      await api.post(`/clients/${cid}/companies-house/sync`, { import_officers: true, directors_only: true });
      toast.success("Updated from Companies House");
      load(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setApplyingId(null); }
  };

  const dismissOne = async (cid) => {
    try { await api.post(`/clients/${cid}/companies-house/dismiss`); loadPending(); }
    catch { toast.error("Could not dismiss"); }
  };

  const chLookup = async (num) => {
    const n = num || chNum;
    if (!n) return toast.error("Enter a company number");
    setChBusy(true);
    try {
      const { data } = await api.get(`/companies-house/company/${n}`);
      setForm((f) => ({
        ...f, legal_name: data.company_name || f.legal_name,
        trading_name: f.trading_name || data.company_name || "",
        company_number: data.company_number, registered_office: data.registered_office_address,
        industry: (data.sic_codes || []).join(", "), year_end: data.year_end || f.year_end,
        cs01_date: data.cs01_next_made_up_to || data.cs01_last_made_up_to || f.cs01_date,
      }));
      setChResults([]);
      toast.success("Company details imported from Companies House");
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setChBusy(false); }
  };
  const chDoSearch = async () => {
    if (!chName) return;
    try { const { data } = await api.get("/companies-house/search", { params: { q: chName } }); setChResults(data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
  };
  const chCancelSearch = () => { setChName(""); setChResults([]); };

  const load = () => api.get("/clients", { params: { q: q || undefined, include_archived: showArchived || undefined } }).then((r) => setClients([...r.data].sort((a, b) => (a.trading_name || a.legal_name || "").localeCompare(b.trading_name || b.legal_name || "", undefined, { sensitivity: "base" }))));
  useEffect(() => { load(); setSel([]); }, [q, showArchived]);

  const exportClients = async () => {
    try {
      const { data } = await api.get("/clients/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "CASS-clients-export.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export clients"); }
  };

  const create = async () => {
    if (!form.legal_name) return toast.error("Legal entity name is required");
    try {
      const { data } = await api.post("/clients", form);
      toast.success("Record created");
      setOpen(false);
      navigate(`/clients/${data.id}`);
    } catch (e) {
      toast.error("Could not create record");
    }
  };

  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allShownIds = clients.map((c) => c.id);
  const allSelected = sel.length > 0 && allShownIds.every((id) => sel.includes(id));
  const toggleSelAll = () => setSel(allSelected ? [] : allShownIds);
  const bulkArchive = async (archived) => {
    if (!sel.length) return;
    try {
      const { data } = await api.post("/clients/bulk-archive", { ids: sel, archived });
      toast.success(`${data.count} record${data.count === 1 ? "" : "s"} ${archived ? "archived" : "restored"}`);
      setSel([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk update failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Clients & Prospects"
        accent="sky"
        Icon={Users}
        subtitle="Manage business records, contacts and relationship information."
        actions={
          canWrite ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportClients} data-testid="export-clients-btn"><Download className="w-4 h-4 mr-2" /> Export</Button>
            {chConfigured && <Button variant="outline" onClick={() => bulkSync(false)} disabled={bulkBusy} data-testid="ch-bulk-sync-btn"><RefreshCw className={"w-4 h-4 mr-2" + (bulkBusy ? " animate-spin" : "")} /> Update from Companies House</Button>}
            <ClientImport onDone={load} />
            <ProposalImport onDone={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
                <UserPlus className="w-4 h-4 mr-2" /> New Record
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="client-dialog">
              <DialogHeader><DialogTitle>New client / prospect</DialogTitle></DialogHeader>
              {chConfigured ? (
                <div className="rounded-md border border-cass-teal/40 bg-cass-teal/5 p-3 space-y-2" data-testid="ch-lookup-block">
                  <div className="text-xs font-semibold text-cass-navy">Auto-fill from Companies House</div>
                  <div className="flex gap-2">
                    <Input className="h-8" placeholder="Company number" value={chNum} onChange={(e) => setChNum(e.target.value)} data-testid="ch-number" />
                    <Button size="sm" disabled={chBusy} onClick={() => chLookup()} className="bg-cass-teal text-white" data-testid="ch-lookup-btn">{chBusy ? "…" : "Look up"}</Button>
                  </div>
                  <div className="flex gap-2">
                    <Input className="h-8" placeholder="…or search by name" value={chName} onChange={(e) => setChName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && chDoSearch()} onBlur={() => setTimeout(() => setChResults([]), 150)} data-testid="ch-search" />
                    <Button size="sm" variant="outline" onClick={chDoSearch}>Search</Button>
                    {(chName || chResults.length > 0) && (
                      <Button size="sm" variant="ghost" onClick={chCancelSearch} data-testid="ch-cancel-btn">Cancel</Button>
                    )}
                  </div>
                  {chResults.map((r) => (
                    <button key={r.company_number} onClick={() => chLookup(r.company_number)} className="block w-full text-left text-xs p-1.5 rounded hover:bg-white" data-testid={`ch-result-${r.company_number}`}>
                      <span className="font-medium text-cass-navy">{r.title}</span> <span className="text-cass-slate">· {r.company_number} · {r.company_status}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-cass-slate">Companies House isn't connected yet — once an API key is added you'll be able to auto-fill company details, year-end and CS01 dates from a company number.</p>
              )}
              <div className="space-y-3">
                <div><Label>Legal entity name *</Label>
                  <Input data-testid="client-legal-name" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></div>
                <div><Label>Trading name</Label>
                  <Input data-testid="client-trading-name" value={form.trading_name} onChange={(e) => setForm({ ...form, trading_name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={form.client_type} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                      <SelectTrigger data-testid="client-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="client">Existing client</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Entity type</Label>
                    <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="limited_company">Limited Company</SelectItem>
                        <SelectItem value="sole_trader">Sole Trader</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="llp">LLP</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-client-btn" onClick={create} className="bg-cass-teal text-white">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          ) : null
        }
      />
      <div className="p-8">
        {chConfigured && chPending.count > 0 && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50" id="ch-pending-banner" data-testid="ch-pending-banner">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <button type="button" className="flex items-center gap-2 text-sm text-amber-900 text-left" onClick={() => setChExpanded((v) => !v)} data-testid="ch-pending-toggle">
                {chExpanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span><b>{chPending.count}</b> {chPending.count === 1 ? "company has" : "companies have"} Companies House updates available — click to review.</span>
              </button>
              {canWrite && <Button size="sm" variant="outline" onClick={() => bulkSync(true)} disabled={bulkBusy} data-testid="ch-review-update-btn">{bulkBusy ? "Working…" : "Accept all changes"}</Button>}
            </div>
            {chExpanded && (
              <div className="border-t border-amber-200 divide-y divide-amber-100" data-testid="ch-pending-list">
                {canWrite && (
                  <div className="px-4 py-2 flex justify-end bg-amber-100/40">
                    <Button size="sm" className="bg-cass-teal text-white" onClick={() => bulkSync(true)} disabled={bulkBusy} data-testid="ch-accept-all-btn">{bulkBusy ? "Applying…" : "Accept all changes"}</Button>
                  </div>
                )}
                {chPending.clients.map((c) => (
                  <div key={c.id} className="px-4 py-3 flex items-start justify-between gap-4" data-testid={`ch-pending-row-${c.id}`}>
                    <div className="text-sm min-w-0">
                      <button className="font-semibold text-cass-navy hover:underline" onClick={() => navigate(`/clients/${c.id}`)}>{c.name}</button>
                      <ul className="mt-1 space-y-0.5 text-xs text-cass-slate">
                        {c.date_changes.map((d, i) => (
                          <li key={i}><span className="font-medium">{d.field}:</span> <span className="line-through opacity-70">{d.old}</span> → <span className="text-amber-800 font-medium">{d.new}</span></li>
                        ))}
                        {c.new_directors.length > 0 && <li><span className="font-medium">New directors:</span> {c.new_directors.join(", ")}</li>}
                        {c.date_changes.length === 0 && c.new_directors.length === 0 && <li className="italic">Changes will be fetched on apply.</li>}
                      </ul>
                    </div>
                    {canWrite && (
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => dismissOne(c.id)} data-testid={`ch-dismiss-${c.id}`}>Dismiss</Button>
                        <Button size="sm" className="bg-cass-teal text-white" onClick={() => applyOne(c.id)} disabled={applyingId === c.id} data-testid={`ch-apply-${c.id}`}>{applyingId === c.id ? "Applying…" : "Apply"}</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative max-w-sm flex-1">
            <Search className="w-4 h-4 absolute left-3 top-3 text-cass-slate" />
            <Input data-testid="client-search" placeholder="Search records…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived">{showArchived ? "Hide archived" : "Show archived"}</Button>
        </div>
        {canWrite && sel.length > 0 && (
          <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-lg border border-cass-teal/40 bg-cass-teal/5" data-testid="bulk-action-bar">
            <span className="text-sm text-cass-navy font-medium">{sel.length} selected</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSel([])} data-testid="bulk-clear-btn">Clear</Button>
              {showArchived
                ? <Button size="sm" variant="outline" onClick={() => bulkArchive(false)} data-testid="bulk-restore-btn"><RotateCcw className="w-4 h-4 mr-1" />Restore selected</Button>
                : <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => bulkArchive(true)} data-testid="bulk-archive-btn"><Archive className="w-4 h-4 mr-1" />Archive selected</Button>}
            </div>
          </div>
        )}
        <div className="bg-white border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-sky-50/70 hover:bg-sky-50/70">
                {canWrite && <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleSelAll} data-testid="select-all-clients" aria-label="Select all" /></TableHead>}
                <TableHead>Business</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Active services</TableHead>
                <TableHead className="text-right">Agreed MRR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`client-row-${c.id}`}
                  className={`cursor-pointer hover:bg-cass-bone ${sel.includes(c.id) ? "bg-cass-teal/5" : ""}`}
                  onClick={() => navigate(`/clients/${c.id}`)}
                >
                  {canWrite && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(c.id)} onCheckedChange={() => toggleSel(c.id)} data-testid={`select-client-${c.id}`} /></TableCell>}
                  <TableCell>
                    <div className="font-medium text-cass-navy flex items-center gap-2">{c.trading_name || c.legal_name}{c.archived && <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">Archived</Badge>}</div>
                    <div className="text-xs text-cass-slate">{c.legal_name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.client_type === "client" ? "default" : "secondary"}>
                      {c.client_type === "client" ? "Client" : "Prospect"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-cass-slate">{(c.active_services || []).length || "—"}</TableCell>
                  <TableCell className="text-right tabular">{c.agreed_monthly_fee ? gbp(c.agreed_monthly_fee) : "—"}</TableCell>
                </TableRow>
              ))}
              {!clients.length && (
                <TableRow><TableCell colSpan={canWrite ? 5 : 4} className="text-center text-cass-slate py-10">No records yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
